import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import './RichTextEditor.css';

const MAX_CHARS = 800;

function ToolbarBtn({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      className={`rte__btn${active ? ' rte__btn--active' : ''}`}
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading:      { levels: [2, 3] },
        codeBlock:    false,
        code:         false,
        blockquote:   false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: 'What makes this tour special? Route highlights, pace, requirements…' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      if (text.length > MAX_CHARS) return;
      onChange(editor.getHTML());
    },
  });

  // TipTap only reads `content` at init — push external value changes (e.g. an
  // AI draft) into the editor. Guarded so the editor's own onUpdate echoes
  // don't loop or reset the cursor while typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const charCount = editor.getText().length;
  const overLimit = charCount > MAX_CHARS;

  return (
    <div className={`rte${overLimit ? ' rte--over' : ''}`}>
      <div className="rte__toolbar">
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
        >
          <em>I</em>
        </ToolbarBtn>
        <div className="rte__sep" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Heading"
        >
          H2
        </ToolbarBtn>
        <div className="rte__sep" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
            <circle cx="3" cy="6" r="1.5" fill="currentColor" stroke="none"/>
            <circle cx="3" cy="12" r="1.5" fill="currentColor" stroke="none"/>
            <circle cx="3" cy="18" r="1.5" fill="currentColor" stroke="none"/>
          </svg>
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Numbered list"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/>
            <text x="1" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">1.</text>
            <text x="1" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">2.</text>
            <text x="1" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">3.</text>
          </svg>
        </ToolbarBtn>
      </div>

      <EditorContent editor={editor} className="rte__content" />

      <div className={`rte__count${overLimit ? ' rte__count--over' : ''}`}>
        {charCount} / {MAX_CHARS}
      </div>
    </div>
  );
}
